import { supabase } from "@/lib/supabaseClient";

import { DISCLAIMER_VERSION, type LegalDocType } from "./legal-constants";

/**
 * SPEC-DOCCONSENT-2 — fetch the current row from `public.legal_documents`
 * for a given document type and compute SHA-256 hashes of legal text in
 * the browser.
 *
 * The signup flow uses these to attach `terms_acceptance` metadata to
 * `supabase.auth.signUp`, which the auth.users trigger then writes into
 * `public.document_consents` alongside the SERVER text it sees. Carrying
 * the CLIENT hash lets us later detect "the user saw a different bytes
 * than what we stored" — useful for audit completeness even though the
 * server is the trusted record.
 */

export interface LegalDocumentRow {
  version: string;
  content: string;
  content_hash: string;
}

/**
 * Read the row marked `is_current = true` for `type`. Returns `null` on
 * any error (network, RLS, no current row) so callers can fall back to
 * a toast + abort without crashing the signup flow.
 */
export async function fetchLegalDocument(
  type: LegalDocType,
): Promise<LegalDocumentRow | null> {
  try {
    const { data, error } = await supabase
      .from("legal_documents")
      .select("version,content,content_hash")
      .eq("document_type", type)
      .eq("is_current", true)
      .single();
    if (error) {
      console.warn("[legal] fetchLegalDocument failed", { type, error });
      return null;
    }
    if (!data) return null;
    return data as LegalDocumentRow;
  } catch (err) {
    console.warn("[legal] fetchLegalDocument threw", { type, err });
    return null;
  }
}

/**
 * SPEC-DOCCONSENT-3b — has the logged-in user recorded a
 * `document_consents` row for the CURRENT disclaimer version?
 *
 * Fails CLOSED: any error path (no session, RLS, network) returns
 * FALSE so the gate stays up. Never bypass the disclaimer on error —
 * KVKK requires an affirmative recorded acceptance per current version.
 */
export async function hasAcceptedDisclaimer(): Promise<boolean> {
  try {
    const { data: userRes, error: userErr } = await supabase.auth.getUser();
    if (userErr) {
      console.warn("[legal] hasAcceptedDisclaimer: getUser failed", userErr);
      return false;
    }
    const userId = userRes?.user?.id;
    if (!userId) return false;

    const { data, error } = await supabase
      .from("document_consents")
      .select("id")
      .eq("user_id", userId)
      .eq("document_type", "disclaimer")
      .eq("document_version", DISCLAIMER_VERSION)
      .limit(1);
    if (error) {
      console.warn(
        "[legal] hasAcceptedDisclaimer: select failed",
        error,
      );
      return false;
    }
    return Array.isArray(data) && data.length > 0;
  } catch (err) {
    console.warn("[legal] hasAcceptedDisclaimer threw", err);
    return false;
  }
}

/**
 * SEC-FIX-2 — record the user's acceptance of the current disclaimer via
 * the SERVER-SIDE RPC `record_document_consent`.
 *
 * SECURITY (audit Bulgu 2): the disclaimer consent used to be inserted
 * directly from the client, which meant `document_content` / `content_hash`
 * / `document_version` were CLIENT-controlled — a malicious client could
 * forge a legal-proof row in its own name. Now the client sends only the
 * document type + version; the SECURITY DEFINER RPC reads the canonical
 * `content` + `content_hash` from `legal_documents` server-side, resolves
 * the caller's identity from `auth.uid()` + `profiles`, and writes the row.
 * The client can no longer influence the stored content/hash/identity.
 *
 * Throws on any failure so the gate's click handler can re-enable the
 * accept button + show a toast (fail-closed; never bypass the gate).
 *
 * The `{fullName, email}` argument is kept for backward-compatible call
 * sites (DisclaimerGate) but is no longer used — the RPC derives identity
 * server-side. It is intentionally ignored.
 */
export async function recordDisclaimerAcceptance(_args?: {
  fullName?: string | null;
  email?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("record_document_consent", {
    p_document_type: "disclaimer",
    p_version: DISCLAIMER_VERSION,
  });
  if (error) {
    throw new Error(error.message || "Failed to record disclaimer acceptance");
  }
}

/**
 * SHA-256 of `text` returned as a lowercase hex string. Uses the Web
 * Crypto API — available on every browser this app targets and on jsdom
 * (vitest's `environment: "jsdom"` test runtime). Encodes via UTF-8
 * because that's what Supabase / Postgres stored.
 */
export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  const view = new Uint8Array(digest);
  let hex = "";
  for (let i = 0; i < view.length; i++) {
    hex += view[i]!.toString(16).padStart(2, "0");
  }
  return hex;
}
